import dayjs from 'dayjs';
import i18n from '@/setup/i18n.js';
import useSessionStore from '@/stores/session';


/**
 * Formats a given date string by the format from the Pimcore SiteSettings.
 *
 * @param {String} date - The date to format.
 * @param {String} inputFormat - The format of the given date.
 *
 * @returns {String}
 */
export function formatDate(date, inputFormat = '') {
  if (!date) {
    return 'invalid Date';
  }

  let internalDate = dayjs(date);

  if (inputFormat) {
    internalDate = dayjs(date, inputFormat);
  }

  if (!internalDate.isValid()) {
    return 'invalid Date';
  }

  const sessionStore = useSessionStore();

  return internalDate.format(sessionStore.dateFormat);
}

/**
 * Returns a formatted course date.
 *
 * @param {Object} course - Expects a course object.
 * @param {String|null} [course.start] - Start date of the course
 * @param {String|null} [course.end] - End date of the course
 * @param {String} [format] - i18n format template name.
 *
 * @returns {String|null}
 */
export function getCourseDateFormatted(course = {}, format = 'dateShort') {
  const startDate = dayjs(course.start || null);
  const endDate = dayjs(course.end || null);

  if (!startDate.isValid()) {
    return null;
  }

  const sessionStore = useSessionStore();
  const dateFormat = sessionStore.dateFormat;

  if (!endDate.isValid() || startDate.isSame(endDate, 'day')) {
    return dateFormat ? formatDate(startDate.toDate()) : i18n.global.d(startDate.toDate(), format);
  }

  return dateFormat
    ? `${formatDate(startDate.toDate())} - ${formatDate(endDate.toDate())}`
    : `${i18n.global.d(startDate.toDate(), format)} - ${i18n.global.d(endDate.toDate(), format)}`;
}

/**
 * Returns a date format string for the currently used localization.
 *
 * @param {String} [locale] - Allows to use an alternative locale. By default, the computer systems local is used.
 *
 * @returns {String}
 */
export function getLocalizedDateFormat(locale = i18n.global.locale) {
  const customDate = new Date(2222, 11, 18);
  const strDate = customDate.toLocaleDateString(locale);

  return strDate.replace('12', 'MM').replace('18', 'DD').replace('2222', 'YYYY');
}
